/**
 * DVD-style bouncing cursor on Home — same look as Pause/Game Over showcase
 * (medium radius + equipped cosmetics), with Home obstacle bounce and the
 * existing Home bounce speed.
 */
import { drawEquippedCosmeticOverlays, equippedHidesBaseCursor } from "./cursor-cosmetics";

type BounceOpts = {
  getObstacles: () => DOMRect[];
  getColor: () => string;
  isActive: () => boolean;
};

/** Per-frame velocity at ~60fps (kept from the first Home bounce implementation). */
const SPEED_X = 1.6;
const SPEED_Y = 1.25;

let opts: BounceOpts | null = null;
let enabled = false;
let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let raf = 0;
let lastTs = 0;
let x = 80;
let y = 120;
let vx = SPEED_X;
let vy = SPEED_Y;
let r = 40;

function mediumRadius(): number {
  const m = Math.min(window.innerWidth, window.innerHeight);
  return Math.max(34, Math.min(52, m * 0.058));
}

function ensureCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  if (canvas && ctx) return { canvas, ctx };
  const c = document.createElement("canvas");
  c.id = "homeBounceCursor";
  c.className = "idleShowcaseCursorCanvas homeBounceCursorCanvas";
  c.setAttribute("aria-hidden", "true");
  document.body.appendChild(c);
  const cctx = c.getContext("2d");
  if (!cctx) {
    c.remove();
    return null;
  }
  canvas = c;
  ctx = cctx;
  resizeCanvas();
  return { canvas: c, ctx: cctx };
}

function resizeCanvas() {
  if (!canvas || !ctx) return;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = Math.max(1, Math.floor(w * dpr));
  canvas.height = Math.max(1, Math.floor(h * dpr));
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  r = mediumRadius();
}

function overlaps(
  a: { l: number; t: number; r: number; b: number },
  rect: DOMRect,
): boolean {
  return a.l < rect.right && a.r > rect.left && a.t < rect.bottom && a.b > rect.top;
}

function bounceEdges(w: number, h: number) {
  if (x < r) {
    x = r;
    vx = Math.abs(vx);
  } else if (x > w - r) {
    x = w - r;
    vx = -Math.abs(vx);
  }
  if (y < r) {
    y = r;
    vy = Math.abs(vy);
  } else if (y > h - r) {
    y = h - r;
    vy = -Math.abs(vy);
  }
}

function bounceObstacles() {
  if (!opts) return;
  const box = { l: x - r, t: y - r, r: x + r, b: y + r };
  for (const rect of opts.getObstacles()) {
    if (rect.width < 4 || rect.height < 4) continue;
    if (!overlaps(box, rect)) continue;
    const overlapL = box.r - rect.left;
    const overlapR = rect.right - box.l;
    const overlapT = box.b - rect.top;
    const overlapB = rect.bottom - box.t;
    const minX = Math.min(overlapL, overlapR);
    const minY = Math.min(overlapT, overlapB);
    if (minX < minY) {
      if (overlapL < overlapR) {
        x -= overlapL;
        vx = -Math.abs(vx);
      } else {
        x += overlapR;
        vx = Math.abs(vx);
      }
    } else if (overlapT < overlapB) {
      y -= overlapT;
      vy = -Math.abs(vy);
    } else {
      y += overlapB;
      vy = Math.abs(vy);
    }
  }
}

function drawFrame() {
  if (!ctx || !canvas) return;
  const w = window.innerWidth;
  const h = window.innerHeight;
  ctx.clearRect(0, 0, w, h);
  const color = opts?.getColor() || "#ffffff";
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  if (!equippedHidesBaseCursor()) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  drawEquippedCosmeticOverlays(ctx, x, y, r, { liveCursorColor: color });
  ctx.restore();
}

function setCanvasVisible(on: boolean) {
  if (!canvas) return;
  canvas.classList.toggle("hidden", !on);
  if (!on) {
    ctx?.clearRect(0, 0, window.innerWidth, window.innerHeight);
  }
}

function bounceStep(ts: number) {
  if (!enabled || !opts?.isActive()) {
    setCanvasVisible(false);
    raf = 0;
    lastTs = 0;
    return;
  }
  if (!ensureCanvas()) {
    raf = 0;
    return;
  }
  setCanvasVisible(true);

  if (!lastTs) lastTs = ts;
  let dt = (ts - lastTs) / 1000;
  lastTs = ts;
  if (dt > 0.05) dt = 0.05;
  if (dt < 0) dt = 0;
  // Scale so 1 frame at 60fps ≈ SPEED_X / SPEED_Y px (legacy Home bounce).
  const stepScale = dt * 60;
  x += vx * stepScale;
  y += vy * stepScale;

  const w = window.innerWidth;
  const h = window.innerHeight;
  bounceEdges(w, h);
  bounceObstacles();
  drawFrame();
  raf = requestAnimationFrame(bounceStep);
}

function ensureLoop() {
  if (raf) return;
  if (!enabled) return;
  lastTs = 0;
  raf = requestAnimationFrame(bounceStep);
}

function onResize() {
  if (!canvas || !ctx) return;
  resizeCanvas();
  const w = window.innerWidth;
  const h = window.innerHeight;
  x = Math.max(r, Math.min(w - r, x));
  y = Math.max(r, Math.min(h - r, y));
  if (enabled && opts?.isActive()) drawFrame();
}

export function mountHomeCursorBounce(o: BounceOpts) {
  opts = o;
  ensureCanvas();
  window.addEventListener("resize", onResize);
}

export function setHomeCursorBounceEnabled(on: boolean) {
  enabled = !!on;
  if (!enabled) {
    setCanvasVisible(false);
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    lastTs = 0;
    return;
  }
  ensureCanvas();
  // Preserve direction; re-seed magnitude to the Home bounce speeds.
  vx = vx < 0 ? -SPEED_X : SPEED_X;
  vy = vy < 0 ? -SPEED_Y : SPEED_Y;
  ensureLoop();
}

export function syncHomeCursorBounceVisibility() {
  if (enabled) ensureLoop();
  else {
    setCanvasVisible(false);
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    lastTs = 0;
  }
}
